<template>
    <div>
        <button
            :name="'edit_order_item-' + orderItem.id"
            class="transform hover:scale-110 transition duration-200 ease-in-out focus:outline-none"
            @click="openModal"
        >
            <img :src="imgSrc('1x')" :srcset="imgSrcset" :alt="imgAlt" />
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <PencilAltIcon
                                        class="h-6 w-6 text-nutreance-green"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0 w-full sm:mr-10">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Edit Order Item
                                    </DialogTitle>
                                </div>
                            </div>

                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />
                                    <input
                                        type="hidden"
                                        name="_method"
                                        value="PATCH"
                                    />
                                    <input
                                        type="hidden"
                                        name="order_item_id"
                                        :value="orderItem.id"
                                    />

                                    <div class="sm:col-span-2 sm:px-16 pb-6">
                                        <div class="py-2">
                                            <label
                                                for="product_option_id"
                                                class="block text-sm font-medium text-gray-700"
                                            >
                                                Product Option
                                            </label>

                                            <div
                                                class="mt-1 relative rounded-md shadow-sm"
                                            >
                                                <select
                                                    v-model="
                                                        inputs.product_option_id
                                                    "
                                                    :ref="
                                                        refs.product_option_id
                                                    "
                                                    id="product_option_id"
                                                    name="product_option_id"
                                                    required
                                                    :class="[
                                                        hasError(
                                                            errors,
                                                            'product_option_id',
                                                        )
                                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-14'
                                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                        'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none sm:text-sm',
                                                    ]"
                                                    :aria-invalid="
                                                        hasError(
                                                            errors,
                                                            'product_option_id',
                                                        )
                                                            ? 'true'
                                                            : 'false'
                                                    "
                                                    :aria-describedby="
                                                        hasError(
                                                            errors,
                                                            'product_option_id',
                                                        )
                                                            ? 'product_option_id-error'
                                                            : ''
                                                    "
                                                >
                                                    <option
                                                        v-for="(
                                                            productOption,
                                                            productOptionId
                                                        ) in productOptions"
                                                        :key="productOptionId"
                                                        :value="productOptionId"
                                                    >
                                                        {{
                                                            productOption
                                                                .product.name
                                                        }}
                                                        ({{
                                                            productOption.description
                                                        }})
                                                    </option>
                                                </select>
                                                <div
                                                    v-if="
                                                        hasError(
                                                            errors,
                                                            'product_option_id',
                                                        )
                                                    "
                                                    class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
                                                >
                                                    <ExclamationCircleIcon
                                                        class="h-5 w-5 text-red-500"
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                            </div>
                                            <p
                                                v-if="
                                                    hasError(
                                                        errors,
                                                        'product_option_id',
                                                    )
                                                "
                                                class="mt-2 text-sm text-red-600"
                                                id="product_option_id-error"
                                            >
                                                {{
                                                    error(
                                                        errors,
                                                        "product_option_id",
                                                    )
                                                }}
                                            </p>
                                        </div>

                                        <div class="py-2">
                                            <label
                                                for="quantity"
                                                class="block text-sm font-medium text-gray-700"
                                            >
                                                Quantity
                                            </label>

                                            <div
                                                class="mt-1 relative rounded-md shadow-sm"
                                            >
                                                <input
                                                    v-model="inputs.quantity"
                                                    :ref="refs.quantity"
                                                    name="quantity"
                                                    id="quantity"
                                                    type="text"
                                                    inputmode="numeric"
                                                    required
                                                    :class="[
                                                        hasError(
                                                            errors,
                                                            'quantity',
                                                        )
                                                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500 pr-10'
                                                            : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                                        'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm',
                                                    ]"
                                                    :aria-invalid="
                                                        hasError(
                                                            errors,
                                                            'quantity',
                                                        )
                                                            ? 'true'
                                                            : 'false'
                                                    "
                                                    :aria-describedby="
                                                        hasError(
                                                            errors,
                                                            'quantity',
                                                        )
                                                            ? 'quantity-error'
                                                            : ''
                                                    "
                                                />
                                                <div
                                                    v-if="
                                                        hasError(
                                                            errors,
                                                            'quantity',
                                                        )
                                                    "
                                                    class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                                >
                                                    <ExclamationCircleIcon
                                                        class="h-5 w-5 text-red-500"
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                            </div>
                                            <p
                                                v-if="
                                                    hasError(errors, 'quantity')
                                                "
                                                class="mt-2 text-sm text-red-600"
                                                id="quantity-error"
                                            >
                                                {{ error(errors, "quantity") }}
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        name="yes"
                                        type="submit"
                                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Save
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick, reactive, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import {
    CheckIcon,
    ExclamationCircleIcon,
    PencilAltIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useStaticAsset } from "../../functions/useStaticAsset";
import { useForm } from "../../functions/useForm";
import LoadingIcon from "../LoadingIcon.vue";

const props = defineProps<{
    csrf?: string;
    errors?: any;
    old?: any;
    orderItem?: any;
    productOptions?: any;
}>();

const { error, hasError, oldInput, onSubmit, submitted } = useForm();
const { asset } = useStaticAsset();
const form = ref<any>(null);
const actionUrl = "/user/order-items/" + props.orderItem.id;
const imgAlt = computed(
    () =>
        `${props.orderItem.product_option.product.name} (${props.orderItem.product_option.product.description})`,
);
const imgSrcset = computed(
    () => `${imgSrc("1x")} 1x, ${imgSrc("2x")} 2x, ${imgSrc("3x")} 3x`,
);
const orderItemHasError =
    props.orderItem.id === parseInt(props.old.order_item_id);
const open = ref(
    Object.prototype.hasOwnProperty.call(props.old, "order_item_id") &&
        Object.prototype.hasOwnProperty.call(props.old, "product_option_id") &&
        Object.prototype.hasOwnProperty.call(props.old, "quantity") &&
        orderItemHasError,
);

const refs = {
    product_option_id: ref<any>(null),
    quantity: ref<any>(null),
};

const inputs = reactive({
    product_option_id:
        oldInput(props.old, "product_option_id") && orderItemHasError
            ? oldInput(props.old, "product_option_id")
            : props.orderItem.product_option_id,
    quantity:
        oldInput(props.old, "quantity") && orderItemHasError
            ? oldInput(props.old, "quantity")
            : props.orderItem.quantity,
});

function imgSrc(size: string) {
    const slug = props.orderItem.product_option.product.slug;
    const upc = props.orderItem.product_option.upc;
    const image = props.orderItem.product_option.image.replace(
        "@1x",
        `@${size}`,
    );

    return asset(`/images/products/${slug}/${upc}/${image}`);
}

function openModal() {
    open.value = true;

    nextTick(() => {
        switch (true) {
            case !!hasError(props.errors, "product_option_id"):
                return refs.product_option_id.value.focus();
            case !!hasError(props.errors, "quantity"):
                return refs.quantity.value.focus();
            default:
                return refs.product_option_id.value.focus();
        }
    });
}

onMounted(() => {
    if (open.value) {
        openModal();
    }
});
</script>
